import { useMemo, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { layoutHistory } from '../lib/deck-history.ts';
import type { DeckHistory as DeckHistoryData } from '../lib/deck-page.ts';
import { DeckHistoryD3 } from './DeckHistoryD3.tsx';
import { SectionHeading } from './SectionHeading.tsx';

import { Button } from './ui/button.tsx';

type RendererProps = { layout: ReturnType<typeof layoutHistory> };

function Chart({
  layout,
  children,
}: {
  children: (props: RendererProps) => ReactNode;
  layout: RendererProps['layout'];
}) {
  return (
    <figure className="m-0 min-w-0">
      <div className="w-fit max-w-full overflow-x-auto py-2">
        {children({ layout })}
      </div>
    </figure>
  );
}

export function DeckHistory({
  history,
  isRefreshing = false,
  onRetry,
  refreshError = null,
}: {
  history: DeckHistoryData;
  isRefreshing?: boolean;
  onRetry?: () => void;
  refreshError?: Error | null;
}) {
  const intl = useIntl();
  const layout = useMemo(() => layoutHistory(history), [history]);

  return (
    <section className="grid gap-4">
      <div className="flex items-baseline justify-between gap-4">
        <SectionHeading
          description={intl.formatMessage({
            id: 'web.deckHistory.description',
          })}
          title={intl.formatMessage({ id: 'web.deckHistory.title' })}
        />
        {isRefreshing ? (
          <p className="text-sm text-muted-foreground">
            {intl.formatMessage({ id: 'web.deckHistory.refreshing' })}
          </p>
        ) : null}
      </div>
      {refreshError ? (
        <div className="flex flex-wrap items-center gap-3 text-destructive">
          <span>
            {intl.formatMessage({ id: 'web.deckHistory.refreshError' })}
          </span>
          {onRetry ? (
            <Button
              className="min-h-10 px-3 text-sm"
              onClick={onRetry}
              type="button"
              variant="secondary"
            >
              {intl.formatMessage({ id: 'web.deckHistory.retry' })}
            </Button>
          ) : null}
        </div>
      ) : null}
      {history.events.length === 0 ? (
        <p className="text-muted-foreground">
          {intl.formatMessage({ id: 'web.deckHistory.empty' })}
        </p>
      ) : (
        <>
          <div className="grid gap-5">
            <Chart layout={layout}>
              {({ layout: currentLayout }) => (
                <DeckHistoryD3 layout={currentLayout} />
              )}
            </Chart>
          </div>
        </>
      )}
    </section>
  );
}
